import { classNames } from '@gettactic/helpers/src/css/classNames';
import {
  InteractiveZone,
  InteractiveZoneCircle
} from '@gettactic/helpers/src/map/types';
import React from 'react';
import { getContrastTextColor } from '@gettactic/helpers/src/colors/getContrastTextColor';

export interface CircleProfilePhotoProps extends React.PropsWithChildren {
  zone: InteractiveZone;
  title: string;
  label?: string;
  className?: string;
  isFaded?: boolean;
  bgColor?: string;
}

export function InteractiveActiveSpot({
  zone,
  title,
  children,
  className,
  label,
  isFaded,
  bgColor
}: CircleProfilePhotoProps) {
  const circle = zone.circle;
  const { strokeWidth, transform, cx, cy, r } = circle as InteractiveZoneCircle;

  const groupTransform = React.useMemo(() => {
    if (!transform) return '';

    const pattern =
      /matrix\(.*? 1 1 .*? (\d+(\.\d+)?(e-?\d+)?) (\d+(\.\d+)?(e-?\d+)?)\) rotate\(-90 .*?\)/;
    const match = transform.match(pattern);

    if (match) {
      const x = match[1];
      const y = match[4];
      return `translate(${x} ${y}) rotate(0)`;
    }

    return transform;
  }, [transform]);

  return (
    <g
      transform={zone.mapBuilderContainerTransformation || zone.containerTransformation}
      className={classNames('circle-profile-photo', className)}
      opacity={isFaded ? 0.5 : 1}
    >
      <>
        <circle {...circle} strokeWidth="0" fill="transparent" />
        <title>{title}</title>
        <circle {...circle} stroke="white" fill="transparent" />
        <circle
          {...circle}
          r={r - strokeWidth / 2}
          strokeWidth="2"
          stroke="#9CA3AF"
          fill="transparent"
          style={
            bgColor
              ? {
                  fill: bgColor
                }
              : undefined
          }
        />
        <circle
          className="wrapper-circle"
          {...circle}
          stroke="white"
          fill="transparent"
          style={
            bgColor
              ? {
                  fill: bgColor
                }
              : undefined
          }
        />
        <g transform={groupTransform}>
          <text
            x={cx}
            y={transform ? (cy ?? 1 + 2) : (cy ?? 1 + r / 6) + 2}
            textAnchor="middle"
            fontSize={r / 1.3}
            fill={bgColor ? getContrastTextColor(bgColor) : 'white'}
            dominantBaseline="middle"
          >
            {label || title}
          </text>
        </g>
      </>
      {children}
    </g>
  );
}
